import Calendar from "common/Calendars/Calendar.tsx";
import React, { useMemo } from "react";
import Drawer from "common/Drawers/Drawer.tsx";
import { BlacklistedOperationHours, LanguageNameSpaces } from "@shared/types";
import { convertToLocalEvents } from "common/Calendars/utils.ts";
import { OPERATION_HOURS_TITLE } from "@shared/features/bots/BotForm/common/constants.ts";
import Title from "common/Typography/Title.tsx";
import Text from "common/Typography/Text.tsx";
import moment from "moment-timezone";
import styled from "styled-components";
import useMeasure from "utils/hooks/useMeasure.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface ViewOnlyOperationHoursProps {
  open: boolean;
  closeDrawer: () => void;
  blacklistedOperationHours?: BlacklistedOperationHours[];
}

const ViewOnlyOperationHours = ({
  open,
  closeDrawer,
  blacklistedOperationHours,
}: ViewOnlyOperationHoursProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const [measureRef, { height: topSectionHeight }] =
    useMeasure<HTMLDivElement>();

  const events = useMemo(() => {
    return convertToLocalEvents(blacklistedOperationHours || []);
  }, [blacklistedOperationHours]);

  const timezone = blacklistedOperationHours?.[0]?.timezone;

  const topSection = (
    <div ref={measureRef}>
      <TopSection>
        <DrawerTitle>{t(OPERATION_HOURS_TITLE)}</DrawerTitle>
        {timezone && (
          <>
            <Text>Timezone</Text>
            <Timezone>
              {timezone}
              <Text type="secondary" style={{ marginLeft: 4 }}>
                (GMT{moment.tz(timezone).format("Z")})
              </Text>
            </Timezone>
          </>
        )}
      </TopSection>
    </div>
  );

  return (
    <StyledDrawer
      open={open}
      onClose={closeDrawer}
      size={800}
      styles={{ body: { padding: 0, overflow: "hidden", height: "100%" } }}
      zIndex={1800}
    >
      {topSection}
      <div style={{ height: `calc(100% - ${topSectionHeight}px)` }}>
        <Calendar
          viewOnly={true}
          myEvents={events}
          popoverTitle="Disabled Trading Hours"
        />
      </div>
    </StyledDrawer>
  );
};

export default ViewOnlyOperationHours;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding-bottom: 0 !important;
  }
`;
const TopSection = styled.div`
  padding: 20px;
`;
const DrawerTitle = styled(Title).attrs({ level: 3 })`
  text-align: center;
  margin-bottom: 20px;
`;
const Timezone = styled(Text)`
  background: ${(p) => p.theme.colors.statisticsBgColor};
  padding: 6px 12px;
  border-radius: 6px;
  margin-top: 8px;
  display: flex;
`;
